import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'

export type ExportJobStatus =
  'idle' | 'starting' | 'pending' | 'processing' | 'completed' | 'failed'

export type ExportErrorCode =
  | 'too_many'
  | 'invalid_params'
  | 'not_found'
  | 'generic'
  | 'status_check_failed'
  | 'server_error'

interface ExportJobResponse {
  id: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  row_count?: number
  file_name?: string
  download_url?: string
  started_at?: string
  completed_at?: string
  error?: string
}

export interface StartCallsExportParams {
  startDate: Date
  endDate: Date
  pbxIds?: number[]
  direction?: 'inbound' | 'outbound'
  answered?: boolean
  hasVoicemail?: boolean
}

interface CallsExportContextValue {
  status: ExportJobStatus
  error: ExportErrorCode | undefined
  rowCount: number | undefined
  isRowCountTruncated: boolean
  isExporting: boolean
  startExport: (params: StartCallsExportParams) => Promise<void>
  reset: () => void
}

const INITIAL_POLL_INTERVAL_MS = 2000
const BACKOFF_POLL_INTERVAL_MS = 5000
const BACKOFF_THRESHOLD_MS = 30000
const MAX_POLL_DURATION_MS = 15 * 60 * 1000
const MAX_ROW_COUNT = 1_000_000

const ALLOWED_DOWNLOAD_HOSTS = ['.amazonaws.com', '.cloudfront.net']

function isAllowedDownloadUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return false
    return ALLOWED_DOWNLOAD_HOSTS.some((host) => parsed.hostname.endsWith(host))
  } catch {
    return false
  }
}

function triggerFileDownload(url: string, fileName?: string) {
  const link = document.createElement('a')
  link.href = url
  link.rel = 'noopener noreferrer'
  link.setAttribute('download', fileName?.split('/').pop() ?? '')
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const errorToastKey = {
  too_many: 'calls.export.errorTooMany',
  invalid_params: 'calls.export.errorInvalid',
  not_found: 'calls.export.errorNotFound',
  generic: 'calls.export.errorGeneric',
  status_check_failed: 'calls.export.errorStatusCheck',
  server_error: 'calls.export.error',
} as const

const defaultContextValue: CallsExportContextValue = {
  status: 'idle',
  error: undefined,
  rowCount: undefined,
  isRowCountTruncated: false,
  isExporting: false,
  startExport: async () => {},
  reset: () => {},
}

const CallsExportContext =
  createContext<CallsExportContextValue>(defaultContextValue)

export const useCallsExport = () => useContext(CallsExportContext)

interface CallsExportProviderProps {
  children: ReactNode
}

export const CallsExportProvider: React.FC<CallsExportProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation()
  const api = useMangoApiClient()
  const { pbxId } = useUserState()
  const { toastSuccess, toastError } = useToastAlert()

  const [status, setStatus] = useState<ExportJobStatus>('idle')
  const [error, setError] = useState<ExportErrorCode | undefined>()
  const [rowCount, setRowCount] = useState<number | undefined>()
  const [isRowCountTruncated, setIsRowCountTruncated] = useState(false)

  const jobIdRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startTimeRef = useRef<number>(0)
  const hasShownProcessingToast = useRef(false)
  const pollStatusRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const EXPORT_TOAST_ID = 'calls-export-progress'

  const showProcessingToast = useCallback(() => {
    if (hasShownProcessingToast.current) return
    hasShownProcessingToast.current = true
    toaster.create({
      id: EXPORT_TOAST_ID,
      type: 'loading',
      title: t('calls.export.toastProcessingTitle'),
      description: t('calls.export.toastProcessingDescription'),
      duration: Infinity,
      meta: { closable: true },
    })
  }, [t])

  const dismissProcessingToast = useCallback(() => {
    toaster.dismiss(EXPORT_TOAST_ID)
  }, [])

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearPollTimer()
  }, [clearPollTimer])

  const pollStatus = useCallback(async () => {
    if (!jobIdRef.current) return

    const capturedJobId = jobIdRef.current
    const elapsed = Date.now() - startTimeRef.current
    if (elapsed > MAX_POLL_DURATION_MS) {
      clearPollTimer()
      setStatus('failed')
      setError('status_check_failed')
      dismissProcessingToast()
      toastError(
        t('calls.export.error'),
        t(errorToastKey['status_check_failed']),
      )
      return
    }

    try {
      const response = await api.calls.getCallsExportStatus(capturedJobId)

      if (jobIdRef.current !== capturedJobId) return

      if (!response.ok) {
        clearPollTimer()
        const errCode: ExportErrorCode =
          response.status === 404 ? 'not_found' : 'status_check_failed'
        setStatus('failed')
        setError(errCode)
        dismissProcessingToast()
        toastError(t('calls.export.error'), t(errorToastKey[errCode]))
        return
      }

      const data = response.data as ExportJobResponse

      switch (data.status) {
        case 'completed': {
          setStatus('completed')
          setRowCount(data.row_count ?? 0)
          setIsRowCountTruncated((data.row_count ?? 0) >= MAX_ROW_COUNT)
          dismissProcessingToast()

          if (!data.download_url || !isAllowedDownloadUrl(data.download_url)) {
            setStatus('failed')
            setError('server_error')
            toastError(
              t('calls.export.error'),
              t(errorToastKey['server_error']),
            )
            return
          }

          if ((data.row_count ?? 0) >= MAX_ROW_COUNT) {
            toastSuccess(t('calls.export.successTruncated'))
          } else {
            toastSuccess(t('calls.export.success'))
          }
          triggerFileDownload(data.download_url, data.file_name) // Uses a hidden anchor tag to download the file
          break
        }
        case 'failed': {
          setStatus('failed')
          setError('server_error')
          dismissProcessingToast()
          toastError(t('calls.export.error'), t(errorToastKey['server_error']))
          break
        }
        case 'pending':
        case 'processing': {
          setStatus(data.status)
          showProcessingToast()
          const interval =
            elapsed > BACKOFF_THRESHOLD_MS
              ? BACKOFF_POLL_INTERVAL_MS
              : INITIAL_POLL_INTERVAL_MS
          pollTimerRef.current = setTimeout(
            () => pollStatusRef.current(),
            interval,
          )
          break
        }
      }
    } catch {
      clearPollTimer()
      setStatus('failed')
      setError('status_check_failed')
      dismissProcessingToast()
      toastError(
        t('calls.export.error'),
        t(errorToastKey['status_check_failed']),
      )
    }
  }, [
    api,
    clearPollTimer,
    dismissProcessingToast,
    showProcessingToast,
    t,
    toastError,
    toastSuccess,
  ])

  pollStatusRef.current = pollStatus

  const startExport = useCallback(
    async (params: StartCallsExportParams) => {
      const exportPbxIds = params.pbxIds ?? (pbxId ? [pbxId] : [])
      if (exportPbxIds.length === 0) return

      setStatus('starting')
      setError(undefined)
      setRowCount(undefined)
      setIsRowCountTruncated(false)
      hasShownProcessingToast.current = false
      clearPollTimer()

      const body: {
        startDate: string
        endDate: string
        pbxIds: number[]
        direction?: 'inbound' | 'outbound'
        answered?: boolean
        hasVoicemail?: boolean
      } = {
        startDate: params.startDate.toISOString(),
        endDate: params.endDate.toISOString(),
        pbxIds: exportPbxIds,
      }

      if (params.direction) {
        body.direction = params.direction
      }
      if (params.answered !== undefined) {
        body.answered = params.answered
      }
      if (params.hasVoicemail !== undefined) {
        body.hasVoicemail = params.hasVoicemail
      }

      try {
        const response = await api.calls.startCallsExport(body)

        if (jobIdRef.current !== null) return

        if (!response.ok) {
          let errCode: ExportErrorCode = 'generic'
          if (response.status === 429) errCode = 'too_many'
          else if (response.status === 400) errCode = 'invalid_params'
          setStatus('failed')
          setError(errCode)
          toastError(t('calls.export.error'), t(errorToastKey[errCode]))
          return
        }

        const data = response.data as { job_id: string }
        jobIdRef.current = data.job_id
        startTimeRef.current = Date.now()
        setStatus('pending')
        showProcessingToast()
        pollTimerRef.current = setTimeout(
          () => pollStatusRef.current(),
          INITIAL_POLL_INTERVAL_MS,
        )
      } catch {
        setStatus('failed')
        setError('generic')
        toastError(t('calls.export.error'), t(errorToastKey['generic']))
      }
    },
    [pbxId, api, clearPollTimer, showProcessingToast, t, toastError],
  )

  const reset = useCallback(() => {
    clearPollTimer()
    dismissProcessingToast()
    jobIdRef.current = null
    setStatus('idle')
    setError(undefined)
    setRowCount(undefined)
    setIsRowCountTruncated(false)
  }, [clearPollTimer, dismissProcessingToast])

  const isExporting =
    status === 'starting' || status === 'pending' || status === 'processing'

  const value: CallsExportContextValue = {
    status,
    error,
    rowCount,
    isRowCountTruncated,
    isExporting,
    startExport,
    reset,
  }

  return (
    <CallsExportContext.Provider value={value}>
      {children}
    </CallsExportContext.Provider>
  )
}
